import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { useAuth } from "src/context/AuthContext";
import { PostAuthService, PostAuthFlowParams, PostAuthFlowResult } from "src/services/postAuthService";

/**
 * Custom hook for handling post-authentication flows
 * Provides a consistent interface for both OAuth and non-OAuth authentication
 */
export const usePostAuthFlow = () => {
  const navigate = useNavigate();
  const { refreshUserData } = useAuth();

  /**
   * Execute the complete post-authentication flow
   */
  const executePostAuthFlow = useCallback(async (params: {
    user: User;
    oauthUserType?: string | null;
    source?: string | null;
  }): Promise<PostAuthFlowResult> => {
    const flowParams: PostAuthFlowParams = {
      ...params,
      navigate,
      refreshUserData,
    };

    return await PostAuthService.executePostAuthFlow(flowParams);
  }, [navigate, refreshUserData]);

  /**
   * Handle OAuth authentication flow specifically
   */
  const handleOAuthFlow = useCallback(async (params: {
    user: User;
    oauthUserType: string;
  }): Promise<PostAuthFlowResult> => {
    return await executePostAuthFlow({
      user: params.user,
      oauthUserType: params.oauthUserType,
    });
  }, [executePostAuthFlow]);

  /**
   * Handle regular (non-OAuth) authentication flow
   */
  const handleRegularAuthFlow = useCallback(async (params: {
    user: User;
    source?: string | null;
  }): Promise<PostAuthFlowResult> => {
    return await executePostAuthFlow({
      user: params.user,
      source: params.source,
    });
  }, [executePostAuthFlow]);

  /**
   * Handle authentication errors
   */
  const handleAuthError = useCallback((error: any) => {
    PostAuthService.handleAuthError(error, navigate);
  }, [navigate]);

  return {
    executePostAuthFlow,
    handleOAuthFlow,
    handleRegularAuthFlow,
    handleAuthError,
  };
};

export default usePostAuthFlow;